import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../../loading-page';
import { useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { DataImportMappingTile } from '../../../presentation-components/settings-components/data-import/data-import-mapping-tile';
import { createHeaderSelection, isConstantSelection, isHeaderSelection, MappingSelection } from '../../../../shared/utils/mapping-selection.utils';

const fileUploadService = new FileUploadService();

export const SuperAdminConversionFactorsMappingPage = () => {
  const navigate = useNavigate();
  const { conversionSourceId, year, uploadId } = useParams();
  //Services
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [gaiaFields, setGaiaFields] = useState<string[]>([]);
  const [fileUploadMapped, setFileUploadMapped] = useState<any>(null)
  const [headings, setHeadings] = useState<string[]>([]);
  const [mapping, setMapping] = useState<{ [gaiaField: string]: MappingSelection[] }>({})
  const [error, setError] = useState<string | null>(null)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);


  const hasMappedSelection = (selections?: MappingSelection[]) => {
    if (!selections || selections.length === 0) return false
    return selections.some(selection => {
      if (isHeaderSelection(selection)) return selection.value.trim() !== ''
      if (isConstantSelection(selection)) return Boolean((selection.displayValue ?? selection.value)?.toString().trim())
      return false
    })
  }

  const mandatoryFields = useMemo<string[]>(() => {
    const opts = (fileUploadMapped?.options || []) as Array<any>
    return opts.filter(o => String(o.status).toLowerCase() === 'mandatory').map(o => o.name)
  }, [fileUploadMapped])
  const missingMandatory = useMemo<string[]>(() => {
    return mandatoryFields.filter(field => !hasMappedSelection(mapping[field]))
  }, [mandatoryFields, mapping])


  useEffect(() => {
    if (!uploadId) return
    setIsLoading(true)
    setError(null)
    fileUploadService.getUploadFileForMapping(uploadId)
      .then(response => {
        if (response?.code !== undefined) {
          setError(response?.message || 'Unable to load mapping data')
          setFileUploadMapped(null)
          setHeadings([])
          setGaiaFields([])
          setMapping({})
          return
        }
        setFileUploadMapped(response)
        setHeadings(response.headers || [])
        setGaiaFields(
          (response.options || [])
            .map((o: any) => o?.name)
            .filter((name: string | undefined): name is string => Boolean(name))
        )
        if (response.conversionSource?.mappings?.length > 0) {
          const formatted: { [gaiaField: string]: MappingSelection[] } = {}
          response.conversionSource.mappings.forEach((m: { mappedHeading: string; customHeading: string }) => {
            if (!formatted[m.mappedHeading]) formatted[m.mappedHeading] = []
            formatted[m.mappedHeading].push(createHeaderSelection(m.customHeading))
          })
          setMapping(formatted)
        } else {
          setMapping({})
        }
      })
      .catch((err) => {
        console.log(err)
        setFileUploadMapped(null)
        setHeadings([])
        setGaiaFields([])
        setMapping({})
        setError(err?.response?.data?.message || err?.message || 'There was an error, please try again')
      })
      .finally(() => setIsLoading(false))
  }, [uploadId])

  const handleMappingChange = (gaiaField: string, selections: MappingSelection[] = []) => {
    const arr = Array.isArray(selections) ? selections : []
    setMapping(prev => {
      const next = { ...prev }
      if (!arr.length) {
        delete next[gaiaField]
      } else {
        next[gaiaField] = arr
      }
      return next
    })
  };

  const handleSubmit = () => {
    if (missingMandatory.length > 0) return
    setIsSubmitting(true);
    const mappingArray: { customHeading: string; mappedHeading: string; isGlobalValue?: boolean }[] = []
    for (const gaiaField in mapping) {
      const selections = mapping[gaiaField] || []
      selections.forEach(selection => {
        if (isConstantSelection(selection)) {
          const customHeading = selection.value?.toString()
          if (!customHeading) return
          mappingArray.push({ customHeading, mappedHeading: gaiaField, isGlobalValue: true })
          return
        }
        if (isHeaderSelection(selection)) {
          mappingArray.push({ customHeading: selection.value, mappedHeading: gaiaField })
        }
      })
    }
    fileUploadService.uploadFileMappings({ fileUploadId: uploadId!, mappings: mappingArray })
      .then(() => {
        setIsSubmitting(false);
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        navigate(`/super-admin/conversion-factors/${conversionSourceId}/${year}?tab=pending`);
      })
      .catch(() => {
        setIsSubmitting(false);
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      });
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title='Map Conversion Factors'
        includeBackButton={true}
        backButtonLink={`/super-admin/conversion-factors/${conversionSourceId}`}
        backButtonText='Back to Years'
      />
      <LoadingPage pageReady={!isLoading}>
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {fileUploadMapped && !error &&
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Required fields</Typography>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                {gaiaFields
                  .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status === 'mandatory')
                  .map((gaiaField: string, index: number) => (
                    <Grid item lg={4} md={6} sm={12} xs={12} key={`req-${index}`}>
                      <DataImportMappingTile
                        value={mapping[gaiaField] || []}
                        selectedValues={mapping}
                        header={gaiaField}
                        fileUpload={fileUploadMapped}
                        handleChange={handleMappingChange}
                        optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                        multiple={false}
                      />
                    </Grid>
                  ))}
              </Grid>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Optional fields</Typography>
              <Grid container spacing={4}>
                {gaiaFields
                  .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status !== 'mandatory')
                  .map((gaiaField: string, index: number) => (
                    <Grid item lg={4} md={6} sm={12} xs={12} key={`opt-${index}`}>
                      <DataImportMappingTile
                        value={mapping[gaiaField] || []}
                        selectedValues={mapping}
                        header={gaiaField}
                        fileUpload={fileUploadMapped}
                        handleChange={handleMappingChange}
                        optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                        multiple={false}
                      />
                    </Grid>
                  ))}
              </Grid>
            </>}
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate(`/super-admin/conversion-factors/${conversionSourceId}`)}
              submitHandler={handleSubmit}
            />
          </Stack>
        </Box>
      </LoadingPage>
    </PageOuterContainer>
  );
}

